import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookLoanBadge, BookLoanMenuItems, LendBookModal } from '@/components/loans'
import { useUpdateBook } from '@/hooks/books'
import { useBookGenres } from '@/hooks/genres'
import { captureError } from '@/lib/monitoring'
import { todayISO } from '@/lib/utils'
import type { Book, UpdateBook } from '@/types/book'
import {
  type BookCardConfig,
  type BookCardContext,
  getBookCardConfig,
  getSmartQuickActions,
} from './BookCard.config'

interface BookCardProps {
  book: Book
  onEdit?: (book: Book) => void
  onDelete?: (book: Book) => void
  context?: BookCardContext // Context-aware configuration
  config?: BookCardConfig // Or custom configuration
  // Selection state for roulette context
  isSelected?: boolean
  onToggleSelection?: () => void
}

export function BookCard({
  book,
  onEdit,
  onDelete,
  context = 'library',
  config,
  isSelected = false,
  onToggleSelection,
}: BookCardProps) {
  const { t } = useTranslation()
  const { data: bookGenres, isLoading: genresLoading } = useBookGenres(book.id)
  const updateBookMutation = useUpdateBook()
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [lending, setLending] = useState(false)

  // Get configuration - use provided config or get from context
  const cardConfig = config || getBookCardConfig(context)
  const { actions, layout } = cardConfig

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'read':
        return 'bg-success text-white border-success'
      case 'reading':
        return 'bg-primary text-primary-foreground border-primary'
      case 'wishlist':
        return 'bg-purple text-white border-purple'
      default:
        return 'bg-muted text-foreground border-border'
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    try {
      const updateData: UpdateBook = {
        status: newStatus as 'unread' | 'reading' | 'read' | 'wishlist',
      }

      // If marking as read and no end_date, set it to today
      if (newStatus === 'read' && !book.end_date) {
        updateData.end_date = todayISO()
      }

      // If marking as reading and no start_date, set it to today
      if (newStatus === 'reading' && !book.start_date) {
        updateData.start_date = todayISO()
      }

      await updateBookMutation.mutateAsync({
        bookId: book.id,
        data: updateData,
      })
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update book status' })
    }
  }

  const handleRatingChange = async (rating: number | null) => {
    try {
      await updateBookMutation.mutateAsync({
        bookId: book.id,
        data: { rating },
      })
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update book rating' })
    }
  }

  const handleDateChange = async (dateType: 'start_date' | 'end_date', date: string | null) => {
    try {
      await updateBookMutation.mutateAsync({
        bookId: book.id,
        data: { [dateType]: date },
      })
    } catch (error) {
      captureError(error as Error, { context: `Failed to update ${dateType}` })
    }
  }

  const handleCardClick = () => {
    if (layout.showSelectionIndicator && onToggleSelection) {
      onToggleSelection()
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Using div instead of button to fix layout issues with aspect-ratio
    // biome-ignore lint/a11y/useKeyWithClickEvents: Click handling is only for optional selection functionality
    <div
      className={`group relative bg-card rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border overflow-hidden text-left w-full ${
        layout.enforceUniformHeight ? (context === 'roulette' ? 'h-96' : 'h-80') : ''
      } ${isSelected ? 'border-purple ring-2 ring-purple/20' : 'border-border'} ${
        layout.showSelectionIndicator && onToggleSelection ? 'cursor-pointer' : ''
      }`}
      onClick={handleCardClick}
      {...(layout.showSelectionIndicator && onToggleSelection
        ? { 'aria-label': `Toggle selection for ${book.title}` }
        : {})}
    >
      {/* Book Cover or Placeholder */}
      <div
        className={`${layout.imageAspectRatio === 'square' ? 'aspect-square' : 'aspect-[3/4]'} bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center relative overflow-hidden`}
      >
        {book.cover_url ? (
          <img src={book.cover_url} alt={book.title} className="w-full h-full object-contain p-4" />
        ) : (
          <div className="text-center p-4 min-w-0 max-w-full">
            <div className="text-muted-foreground mb-2">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                />
              </svg>
            </div>
            <p className="text-xs font-medium text-muted-foreground line-clamp-2 break-words">
              {book.title}
            </p>
          </div>
        )}

        {/* Selection Indicator - Left */}
        {layout.showSelectionIndicator && layout.selectionIndicatorPosition === 'left' && (
          <div className="absolute top-2 left-2 z-10">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                isSelected ? 'bg-purple border-purple text-white' : 'bg-card border-input'
              }`}
            >
              {isSelected && (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* Status Badge - Left */}
        {layout.showStatusBadge && (
          <div
            className={`absolute top-2 ${
              layout.showSelectionIndicator && layout.selectionIndicatorPosition === 'left'
                ? 'left-10'
                : 'left-2'
            }`}
          >
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(book.status)}`}
            >
              {t(`books.status.${book.status}`)}
            </span>
          </div>
        )}

        {/* Book Type Badge - Fixed position for roulette context, shows on hover */}
        {context === 'roulette' && (
          <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border shadow-sm backdrop-blur-sm ${
                book.type === 'digital'
                  ? 'bg-purple/90 text-white border-purple'
                  : 'bg-amber/90 text-white border-amber'
              }`}
            >
              {book.type === 'digital' ? (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                  />
                </svg>
              )}
              {t(`books.type.${book.type || 'physical'}`)}
            </span>
          </div>
        )}

        {/* Quick Actions Menu Button - Right */}
        {layout.showQuickActionsMenu && (
          <div className="absolute top-2 right-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setShowQuickActions(!showQuickActions)
              }}
              className="bg-card/95 backdrop-blur-sm text-foreground p-2 sm:p-1.5 rounded-lg hover:bg-card shadow-sm border border-border/50 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 touch-manipulation"
            >
              <svg
                className="w-5 h-5 sm:w-4 sm:h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Selection Indicator - Right */}
        {layout.showSelectionIndicator && layout.selectionIndicatorPosition === 'right' && (
          <div className="absolute top-2 right-10 z-10">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                isSelected ? 'bg-purple border-purple text-white' : 'bg-card border-input'
              }`}
            >
              {isSelected && (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Book Information */}
      <div
        className={`p-4 ${layout.compactMode ? 'space-y-2' : 'space-y-3'} ${layout.enforceUniformHeight ? 'flex-1 flex flex-col' : ''}`}
      >
        {/* Title and Author */}
        <div className="min-h-0">
          <h3 className="font-semibold text-foreground line-clamp-2 leading-tight mb-1 break-words">
            {book.title}
          </h3>
          <p
            className={`text-sm text-muted-foreground ${context === 'roulette' ? 'line-clamp-2' : 'line-clamp-1'} break-words`}
          >
            {t('common.by')} {book.author}
          </p>
          {context !== 'roulette' && <BookLoanBadge book={book} />}
        </div>

        {/* Rating - Only for read books */}
        {layout.showRating && book.status === 'read' && (
          <div className="flex items-center gap-1">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((starNum) => (
                <button
                  type="button"
                  key={`rating-star-${starNum}`}
                  onClick={() => handleRatingChange(starNum)}
                  disabled={updateBookMutation.isPending}
                  className={`w-4 h-4 transition-colors hover:scale-110 ${
                    starNum <= (book.rating || 0) ? 'text-amber' : 'text-muted hover:text-amber/70'
                  } ${updateBookMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
            </div>
            {book.rating && (
              <span className="text-xs text-muted-foreground ml-1">({book.rating}/5)</span>
            )}
            {!book.rating && (
              <span className="text-xs text-muted-foreground/70 ml-1">
                {t('books.actions.rate')}
              </span>
            )}
          </div>
        )}

        {/* Rating Display for Non-Read Books */}
        {layout.showRating && book.status !== 'read' && book.rating && (
          <div className="flex items-center gap-1">
            <div className="flex text-amber">
              {[1, 2, 3, 4, 5].map((starNum) => (
                <svg
                  key={`display-star-${starNum}`}
                  className={`w-4 h-4 ${starNum <= book.rating! ? 'text-amber' : 'text-muted'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-muted-foreground ml-1">({book.rating}/5)</span>
            <span className="text-xs text-warning ml-1">
              • {t('books.actions.markAsReadToRate')}
            </span>
          </div>
        )}

        {/* Genres */}
        {layout.showGenres && !genresLoading && bookGenres && bookGenres.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {bookGenres.slice(0, 3).map((genre) => (
              <span
                key={genre.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted/50 text-foreground border border-border"
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: genre.color }} />
                {genre.name}
              </span>
            ))}
            {bookGenres.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted/50 text-muted-foreground border border-border">
                +{bookGenres.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Reading Dates - Editable based on status */}
        {layout.showReadingDates && (
          <div className="text-xs text-muted-foreground space-y-1">
            {/* Start Date - Show for reading or read books */}
            {(book.status === 'reading' || book.status === 'read') && (
              <div className="flex items-center gap-1">
                <svg
                  className="w-3 h-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{t('books.fields.startDate')}:</span>
                {book.start_date ? (
                  <input
                    type="date"
                    value={book.start_date}
                    onChange={(e) => handleDateChange('start_date', e.target.value || null)}
                    disabled={updateBookMutation.isPending}
                    className="bg-transparent border-none text-xs text-foreground cursor-pointer hover:bg-muted/20 rounded px-1 focus:outline-none focus:bg-muted/20 disabled:opacity-50"
                    style={{ width: '95px' }}
                  />
                ) : (
                  <div className="relative inline-block">
                    <input
                      type="date"
                      value=""
                      onChange={(e) => handleDateChange('start_date', e.target.value || null)}
                      disabled={updateBookMutation.isPending}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      title={t('common.setDate')}
                    />
                    <svg
                      className="w-4 h-4 text-muted-foreground hover:text-primary hover:scale-110 cursor-pointer transition-all duration-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}

            {/* End Date - Show for read books */}
            {book.status === 'read' && (
              <div className="flex items-center gap-1">
                <svg
                  className="w-3 h-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{t('books.fields.endDate')}:</span>
                {book.end_date ? (
                  <input
                    type="date"
                    value={book.end_date}
                    onChange={(e) => handleDateChange('end_date', e.target.value || null)}
                    disabled={updateBookMutation.isPending}
                    className="bg-transparent border-none text-xs text-foreground cursor-pointer hover:bg-muted/20 rounded px-1 focus:outline-none focus:bg-muted/20 disabled:opacity-50"
                    style={{ width: '95px' }}
                  />
                ) : (
                  <div className="relative inline-block">
                    <input
                      type="date"
                      value=""
                      onChange={(e) => handleDateChange('end_date', e.target.value || null)}
                      disabled={updateBookMutation.isPending}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      title={t('common.setDate')}
                    />
                    <svg
                      className="w-4 h-4 text-muted-foreground hover:text-primary hover:scale-110 cursor-pointer transition-all duration-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ISBN */}
        {layout.showISBN && book.isbn && (
          <div className="text-xs text-muted-foreground/70 font-mono">ISBN: {book.isbn}</div>
        )}

        {/* Notes Preview - Clickable for read books */}
        {layout.showNotes &&
          (book.status === 'read' && layout.showNotesEditor ? (
            <button
              type="button"
              className={`w-full cursor-pointer rounded transition-all ${
                book.notes
                  ? 'text-left border-t border-border pt-3 mt-3 hover:bg-muted/20 -mx-2 px-2 py-1'
                  : 'text-center border-t border-border pt-3 mt-3 hover:bg-muted/20 text-muted-foreground hover:text-primary'
              }`}
              onClick={() => onEdit?.(book)}
            >
              {book.notes ? (
                <div>
                  <p className="text-xs font-medium text-foreground mb-1">
                    {t('books.actions.yourNotes')}:
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2 break-words">
                    {book.notes}
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <span className="text-xs font-medium">
                    {t('books.actions.clickToWriteReview')}
                  </span>
                </div>
              )}
            </button>
          ) : book.notes ? (
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-xs text-muted-foreground line-clamp-2 break-words">
                <span className="font-medium">{t('books.fields.notes')}:</span> {book.notes}
              </p>
            </div>
          ) : null)}
      </div>

      {/* Quick Actions Dropdown */}
      {layout.showQuickActionsMenu && showQuickActions && (
        <>
          {/* Backdrop */}
          <button
            type="button"
            className="fixed inset-0 z-10 bg-transparent border-none cursor-default"
            onClick={() => setShowQuickActions(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowQuickActions(false)
              }
            }}
            aria-label="Close menu"
          />

          {/* Dropdown Menu */}
          <div className="absolute top-12 right-0 sm:right-2 left-0 sm:left-auto mx-2 sm:mx-0 z-20 bg-card rounded-xl shadow-lg border border-border py-2 sm:min-w-48 max-h-[70vh] overflow-y-auto">
            {/* Status Actions - Only show if there are status transitions available */}
            {actions.allowedStatusTransitions.length > 0 && (
              <>
                <div className="px-3 py-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('books.fields.status')}
                  </p>
                </div>

                {actions.allowedStatusTransitions.map((status) => (
                  <button
                    type="button"
                    key={status}
                    onClick={() => {
                      handleStatusChange(status)
                      setShowQuickActions(false)
                    }}
                    disabled={updateBookMutation.isPending || book.status === status}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/20 flex items-center gap-2 transition-colors ${
                      book.status === status
                        ? 'bg-muted/20 text-muted-foreground cursor-not-allowed'
                        : 'text-foreground'
                    } ${updateBookMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${
                        status === 'read'
                          ? 'bg-green-500'
                          : status === 'reading'
                            ? 'bg-blue-500'
                            : status === 'wishlist'
                              ? 'bg-purple-500'
                              : 'bg-gray-400'
                      }`}
                    />
                    {t(`books.status.${status}`)}
                    {book.status === status && (
                      <svg
                        className="w-3 h-3 ml-auto text-muted-foreground"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                ))}

                <div className="border-t border-border my-2" />
              </>
            )}

            {/* Other Actions */}
            <div className="px-3 py-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('books.actions.title')}
              </p>
            </div>

            {/* Clear Rating - Only for read books with rating */}
            {book.status === 'read' && book.rating && (
              <button
                type="button"
                onClick={() => {
                  handleRatingChange(null)
                  setShowQuickActions(false)
                }}
                disabled={updateBookMutation.isPending}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/20 flex items-center gap-2 transition-colors text-foreground ${
                  updateBookMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <svg
                  className="w-4 h-4 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                {t('books.actions.removeRating')}
              </button>
            )}

            {/* Smart Quick Actions based on configuration */}
            {getSmartQuickActions(book, actions).map((action) => (
              <button
                type="button"
                key={action.status}
                onClick={() => {
                  handleStatusChange(action.status)
                  setShowQuickActions(false)
                }}
                disabled={updateBookMutation.isPending}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/20 flex items-center gap-2 transition-colors text-foreground ${
                  updateBookMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {action.icon === 'clock' && (
                  <svg
                    className={`w-4 h-4 ${
                      action.color === 'blue'
                        ? 'text-primary'
                        : action.color === 'green'
                          ? 'text-success'
                          : action.color === 'purple'
                            ? 'text-purple'
                            : action.color === 'orange'
                              ? 'text-warning'
                              : action.color === 'red'
                                ? 'text-error'
                                : 'text-muted-foreground'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {action.icon === 'heart' && (
                  <svg
                    className={`w-4 h-4 ${
                      action.color === 'blue'
                        ? 'text-primary'
                        : action.color === 'green'
                          ? 'text-success'
                          : action.color === 'purple'
                            ? 'text-purple'
                            : action.color === 'orange'
                              ? 'text-warning'
                              : action.color === 'red'
                                ? 'text-error'
                                : 'text-muted-foreground'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                )}
                {action.icon === 'check' && (
                  <svg
                    className={`w-4 h-4 ${
                      action.color === 'blue'
                        ? 'text-primary'
                        : action.color === 'green'
                          ? 'text-success'
                          : action.color === 'purple'
                            ? 'text-purple'
                            : action.color === 'orange'
                              ? 'text-warning'
                              : action.color === 'red'
                                ? 'text-error'
                                : 'text-muted-foreground'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {action.icon === 'refresh' && (
                  <svg
                    className={`w-4 h-4 ${
                      action.color === 'blue'
                        ? 'text-primary'
                        : action.color === 'green'
                          ? 'text-success'
                          : action.color === 'purple'
                            ? 'text-purple'
                            : action.color === 'orange'
                              ? 'text-warning'
                              : action.color === 'red'
                                ? 'text-error'
                                : 'text-muted-foreground'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                {action.icon === 'library' && (
                  <svg
                    className={`w-4 h-4 ${
                      action.color === 'blue'
                        ? 'text-primary'
                        : action.color === 'green'
                          ? 'text-success'
                          : action.color === 'purple'
                            ? 'text-purple'
                            : action.color === 'orange'
                              ? 'text-warning'
                              : action.color === 'red'
                                ? 'text-error'
                                : 'text-muted-foreground'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                    />
                  </svg>
                )}
                {t(action.label)}
              </button>
            ))}

            {/* Write Review - Only for read books */}
            {book.status === 'read' && actions.allowEdit && onEdit && (
              <button
                type="button"
                onClick={() => {
                  onEdit(book)
                  setShowQuickActions(false)
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted/20 flex items-center gap-2 transition-colors text-foreground"
              >
                <svg
                  className="w-4 h-4 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {t('books.actions.writeReview')}
              </button>
            )}

            {/* Edit Action */}
            {actions.allowEdit && onEdit && (
              <button
                type="button"
                onClick={() => {
                  onEdit(book)
                  setShowQuickActions(false)
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted/20 flex items-center gap-2 transition-colors text-foreground"
              >
                <svg
                  className="w-4 h-4 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {t('books.editBook')}
              </button>
            )}

            {/* Lend / mark returned */}
            {context !== 'roulette' && (
              <BookLoanMenuItems
                book={book}
                onLend={() => setLending(true)}
                onDone={() => setShowQuickActions(false)}
              />
            )}

            {/* Delete Action */}
            {actions.allowDelete && onDelete && (
              <button
                type="button"
                onClick={() => {
                  onDelete(book)
                  setShowQuickActions(false)
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-error/5 flex items-center gap-2 transition-colors text-error"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                {t('books.deleteBook')}
              </button>
            )}
          </div>
        </>
      )}

      {lending && <LendBookModal book={book} onClose={() => setLending(false)} />}
    </div>
  )
}
